import React from 'react';
import { IonSegment, IonSegmentButton, IonLabel } from '@ionic/react';
import { MAX_GRP_TXT_RECIPIENTS } from '@/modules/config';
import styles from './AddressingModeSegment.module.css';
import { ShareWizardLogic } from './logic';
import type { AddressingMode } from './types';

/**
 * Direct texting sends one message per recipient, and the control only appears
 * once some intent has more than one, so there are always several of them.
 */
const DIRECT_TEXT_LABEL = 'Direct text msgs';

/**
 * "Group text" while the whole share is one group text — a single intent whose
 * recipients all fit in one leg — and "Group texts" as soon as it would take
 * more than one, either because several intents have recipients or because one
 * intent overflows the leg cap.
 */
function groupTextLabel(recipientCountsByIntent: readonly number[]): string {
  const nonEmpty = recipientCountsByIntent.filter(count => count > 0);
  return nonEmpty.length === 1 && nonEmpty[0] <= MAX_GRP_TXT_RECIPIENTS
    ? 'Group text'
    : 'Group texts';
}

/**
 * The "Share as" choice: `dm` sends a direct text to each recipient, while
 * `group-text` sends one text per leg addressed to several of them.
 *
 * It knows nothing about the flow it sits in. The caller owns the mode and
 * hands over the recipient counts; everything the control says about them —
 * whether to appear at all, and how to word the group option — it works out
 * itself. So the same control serves anywhere the sender can still change how
 * the messages go out.
 */
const AddressingModeSegment: React.FC<{
  mode: AddressingMode;
  onChange: (mode: AddressingMode) => void;
  /** Recipients per intent, in compose-step order. */
  recipientCountsByIntent: readonly number[];
  /** Locks the choice, e.g. once a leg of this share is on its way. */
  disabled?: boolean;
}> = ({ mode, onChange, recipientCountsByIntent, disabled = false }) => {
  // No intent with more than one recipient means a group text would be a text
  // to one person, so there is nothing to choose between.
  if (!ShareWizardLogic.canGroupText(recipientCountsByIntent)) return null;

  return (
    <div className={styles.segment}>
      <p className={styles.heading}>Share as</p>
      <IonSegment
        value={mode}
        disabled={disabled}
        onIonChange={e =>
          onChange(e.detail.value === 'group-text' ? 'group-text' : 'dm')
        }
      >
        <IonSegmentButton
          value="dm"
          data-testid="share-individual-texts-segment"
        >
          <IonLabel className={styles.optionLabel}>
            {DIRECT_TEXT_LABEL}
          </IonLabel>
        </IonSegmentButton>
        <IonSegmentButton
          value="group-text"
          data-testid="share-group-texts-segment"
        >
          <IonLabel className={styles.optionLabel}>
            {groupTextLabel(recipientCountsByIntent)}
          </IonLabel>
        </IonSegmentButton>
      </IonSegment>
    </div>
  );
};

export default AddressingModeSegment;
